import React from 'react';

interface OriginalIPCCFigureDiagramProps {
  figureId: string;
  originalIpccUrl?: string;
}

export const OriginalIPCCFigureDiagram: React.FC<OriginalIPCCFigureDiagramProps> = ({ figureId, originalIpccUrl }) => {
  return (
    <div className="w-full bg-slate-950 p-4 lg:p-6 rounded-2xl border border-slate-700/80 space-y-4 shadow-xl">
      {/* Figure Title Banner */}
      <div className="flex items-center justify-between text-xs font-mono text-slate-300 border-b border-slate-800 pb-2 flex-wrap gap-2">
        <span className="text-amber-300 font-bold tracking-wide">Official IPCC Publication High-Contrast Vector Schematic</span>
        
        {originalIpccUrl && (
          <a
            href={originalIpccUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-cyan-300 hover:text-cyan-200 font-bold underline flex items-center gap-1"
          >
            <span>Open Official IPCC Source ↗</span>
          </a>
        )}
      </div>

      {/* --- HIGH CONTRAST FIGURE SCHEMATIC RENDERERS --- */}

      {/* SPM.1 Diagram */}
      {figureId === 'spm1' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Panel a */}
            <div className="p-3.5 bg-slate-900 rounded-xl border border-slate-700 space-y-2">
              <span className="text-xs font-bold text-white block">Panel (a) Reconstructed Temp over 2000 Years</span>
              <svg viewBox="0 0 300 150" className="w-full h-40 bg-slate-950 rounded-lg">
                {/* Visible Gridlines */}
                <line x1="30" y1="120" x2="280" y2="120" stroke="#475569" strokeDasharray="3,3" strokeWidth="1" />
                <line x1="30" y1="75" x2="280" y2="75" stroke="#475569" strokeDasharray="3,3" strokeWidth="1" />
                <line x1="30" y1="30" x2="280" y2="30" stroke="#475569" strokeDasharray="3,3" strokeWidth="1" />

                {/* High Contrast Uncertainty Band */}
                <path d="M30 75 Q 80 80, 130 85 T 230 80 L 280 30 L 280 45 Q 230 90, 130 95 T 30 85 Z" fill="rgba(148, 163, 184, 0.35)" stroke="#94a3b8" strokeWidth="0.8" />

                {/* Paleo Line */}
                <path d="M30 75 Q 80 82, 130 87 T 230 82 L 280 35" fill="none" stroke="#38bdf8" strokeWidth="3" />

                {/* High Contrast Axis Labels */}
                <text x="35" y="142" fill="#f8fafc" fontSize="10" fontWeight="bold">1 AD</text>
                <text x="140" y="142" fill="#f8fafc" fontSize="10" fontWeight="bold">1000 AD</text>
                <text x="245" y="142" fill="#f8fafc" fontSize="10" fontWeight="bold">2020 AD</text>
                <text x="5" y="78" fill="#e2e8f0" fontSize="9" fontWeight="bold">0.0°C</text>
                <text x="5" y="33" fill="#f43f5e" fontSize="9" fontWeight="bold">+1.5°C</text>
              </svg>
            </div>

            {/* Panel b */}
            <div className="p-3.5 bg-slate-900 rounded-xl border border-slate-700 space-y-2">
              <span className="text-xs font-bold text-white block">Panel (b) 1850–2020 Human vs Natural Drivers</span>
              <svg viewBox="0 0 300 150" className="w-full h-40 bg-slate-950 rounded-lg">
                <line x1="30" y1="110" x2="280" y2="110" stroke="#475569" strokeDasharray="3,3" strokeWidth="1" />
                {/* Natural Band */}
                <path d="M30 110 L 280 110 L 280 118 L 30 115 Z" fill="rgba(52, 211, 153, 0.35)" stroke="#34d399" strokeWidth="1" />
                {/* Human + Natural Band */}
                <path d="M30 110 Q 150 105, 230 60 L 280 25 L 280 40 Q 230 75, 150 115 Z" fill="rgba(244, 63, 94, 0.35)" stroke="#f43f5e" strokeWidth="1" />
                {/* Observed Line */}
                <path d="M30 110 L 100 112 L 170 102 L 230 55 L 280 30" fill="none" stroke="#38bdf8" strokeWidth="3.5" />

                <text x="35" y="142" fill="#f8fafc" fontSize="10" fontWeight="bold">1850</text>
                <text x="150" y="142" fill="#f8fafc" fontSize="10" fontWeight="bold">1950</text>
                <text x="245" y="142" fill="#f8fafc" fontSize="10" fontWeight="bold">2020</text>
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* SPM.2 Diagram */}
      {figureId === 'spm2' && (
        <div className="p-4 bg-slate-900 rounded-xl border border-slate-700 space-y-3">
          <span className="text-xs font-bold text-white block">Radiative Forcing & Temperature Contribution by Species (2019)</span>
          <svg viewBox="0 0 500 200" className="w-full h-48 bg-slate-950 rounded-lg">
            {/* Zero Line */}
            <line x1="200" y1="15" x2="200" y2="185" stroke="#94a3b8" strokeWidth="2.5" />

            {/* CO2 Bar */}
            <rect x="200" y="25" width="180" height="18" fill="#f43f5e" rx="3" stroke="#fda4af" strokeWidth="1" />
            <text x="20" y="38" fill="#f8fafc" fontSize="11" fontWeight="bold">Carbon Dioxide (CO2)</text>
            <text x="390" y="38" fill="#f43f5e" fontSize="11" fontWeight="bold">+2.16 W/m²</text>

            {/* CH4 Bar */}
            <rect x="200" y="55" width="90" height="18" fill="#fb923c" rx="3" stroke="#fed7aa" strokeWidth="1" />
            <text x="20" y="68" fill="#f8fafc" fontSize="11" fontWeight="bold">Methane (CH4)</text>
            <text x="300" y="68" fill="#fb923c" fontSize="11" fontWeight="bold">+0.54 W/m²</text>

            {/* SO2 Aerosol Bar (Cooling - Left) */}
            <rect x="110" y="85" width="90" height="18" fill="#22d3ee" rx="3" stroke="#a5f3fc" strokeWidth="1" />
            <text x="20" y="98" fill="#f8fafc" fontSize="11" fontWeight="bold">Sulfur Dioxide (SO2)</text>
            <text x="35" y="98" fill="#22d3ee" fontSize="11" fontWeight="bold">-0.90 W/m²</text>

            {/* Land Use Bar */}
            <rect x="180" y="115" width="20" height="18" fill="#34d399" rx="3" stroke="#a7f3d0" strokeWidth="1" />
            <text x="20" y="128" fill="#f8fafc" fontSize="11" fontWeight="bold">Land Use & Albedo</text>
            <text x="105" y="128" fill="#34d399" fontSize="11" fontWeight="bold">-0.15 W/m²</text>

            {/* Net Human Forcing Line Marker */}
            <line x1="330" y1="15" x2="330" y2="185" stroke="#facc15" strokeWidth="2.5" strokeDasharray="4,4" />
            <text x="335" y="178" fill="#facc15" fontSize="10" fontWeight="bold">Net Human Forcing (+2.72 W/m²)</text>
          </svg>
        </div>
      )}

      {/* SPM.3 Diagram */}
      {figureId === 'spm3' && (
        <div className="p-4 bg-slate-900 rounded-xl border border-slate-700 space-y-3">
          <span className="text-xs font-bold text-white block">Regional Extremes Synthesis Maps (Hot, Precip, Drought)</span>
          <svg viewBox="0 0 500 160" className="w-full h-44 bg-slate-950 rounded-lg">
            <polygon points="50,40 70,30 90,40 90,60 70,70 50,60" fill="#f43f5e" stroke="#020617" strokeWidth="2" />
            <polygon points="95,40 115,30 135,40 135,60 115,70 95,60" fill="#f43f5e" stroke="#020617" strokeWidth="2" />
            <polygon points="200,40 220,30 240,40 240,60 220,70 200,60" fill="#f43f5e" stroke="#020617" strokeWidth="2" />
            <polygon points="245,40 265,30 285,40 285,60 265,70 245,60" fill="#fbbf24" stroke="#020617" strokeWidth="2" />
            <polygon points="350,40 370,30 390,40 390,60 370,70 350,60" fill="#f43f5e" stroke="#020617" strokeWidth="2" />

            <text x="58" y="53" fill="#ffffff" fontSize="11" fontWeight="bold">NWN</text>
            <text x="103" y="53" fill="#ffffff" fontSize="11" fontWeight="bold">NEN</text>
            <text x="208" y="53" fill="#ffffff" fontSize="11" fontWeight="bold">WEU</text>
            <text x="253" y="53" fill="#ffffff" fontSize="11" fontWeight="bold">EAS</text>
            <text x="358" y="53" fill="#ffffff" fontSize="11" fontWeight="bold">EAU</text>

            <text x="40" y="115" fill="#f43f5e" fontSize="11" fontWeight="bold">● Increase (High Confidence)</text>
            <text x="200" y="115" fill="#fbbf24" fontSize="11" fontWeight="bold">● Increase (Medium Confidence)</text>
            <text x="380" y="115" fill="#cbd5e1" fontSize="11" fontWeight="bold">○ Low Agreement</text>
          </svg>
        </div>
      )}

      {/* SPM.4 Diagram */}
      {figureId === 'spm4' && (
        <div className="p-4 bg-slate-900 rounded-xl border border-slate-700 space-y-3">
          <span className="text-xs font-bold text-white block">Future Climate Projections across 5 SSP Scenarios (2015–2100)</span>
          <svg viewBox="0 0 500 180" className="w-full h-48 bg-slate-950 rounded-lg">
            <line x1="40" y1="140" x2="480" y2="140" stroke="#475569" strokeWidth="1.5" />
            
            {/* SSP5-8.5 Curve */}
            <path d="M40 130 Q 200 120, 320 70 T 480 20" fill="none" stroke="#f43f5e" strokeWidth="3.5" />
            {/* SSP3-7.0 Curve */}
            <path d="M40 130 Q 200 125, 320 85 T 480 40" fill="none" stroke="#fb923c" strokeWidth="3.5" />
            {/* SSP2-4.5 Curve */}
            <path d="M40 130 Q 200 128, 320 100 T 480 75" fill="none" stroke="#fbbf24" strokeWidth="3.5" />
            {/* SSP1-2.6 Curve */}
            <path d="M40 130 Q 200 129, 320 115 T 480 110" fill="none" stroke="#60a5fa" strokeWidth="3.5" />
            {/* SSP1-1.9 Curve */}
            <path d="M40 130 Q 200 128, 320 120 T 480 125" fill="none" stroke="#38bdf8" strokeWidth="3.5" />

            <text x="435" y="25" fill="#f43f5e" fontSize="10" fontWeight="bold">SSP5-8.5 (+4.4°C)</text>
            <text x="435" y="45" fill="#fb923c" fontSize="10" fontWeight="bold">SSP3-7.0 (+3.6°C)</text>
            <text x="435" y="80" fill="#fbbf24" fontSize="10" fontWeight="bold">SSP2-4.5 (+2.7°C)</text>
            <text x="435" y="112" fill="#60a5fa" fontSize="10" fontWeight="bold">SSP1-2.6 (+1.8°C)</text>
            <text x="435" y="130" fill="#38bdf8" fontSize="10" fontWeight="bold">SSP1-1.9 (+1.4°C)</text>

            <text x="40" y="158" fill="#f8fafc" fontSize="10" fontWeight="bold">2015</text>
            <text x="250" y="158" fill="#f8fafc" fontSize="10" fontWeight="bold">2050</text>
            <text x="450" y="158" fill="#f8fafc" fontSize="10" fontWeight="bold">2100</text>
          </svg>
        </div>
      )}

      {/* SPM.5 Diagram */}
      {figureId === 'spm5' && (
        <div className="p-4 bg-slate-900 rounded-xl border border-slate-700 space-y-3">
          <span className="text-xs font-bold text-white block">Global Latitude Warming & Polar Amplification Schematics</span>
          <svg viewBox="0 0 500 150" className="w-full h-40 bg-slate-950 rounded-lg">
            <path d="M30 110 Q 150 115, 300 90 T 470 20 L 470 130 L 30 130 Z" fill="rgba(244, 63, 94, 0.3)" stroke="#f43f5e" strokeWidth="1" />
            <path d="M30 110 Q 150 115, 300 90 T 470 20" fill="none" stroke="#f43f5e" strokeWidth="3.5" />

            <text x="35" y="145" fill="#f8fafc" fontSize="10" fontWeight="bold">-80°S (Antarctica)</text>
            <text x="230" y="145" fill="#f8fafc" fontSize="10" fontWeight="bold">0° (Equator)</text>
            <text x="360" y="145" fill="#f43f5e" fontSize="10" fontWeight="bold">+80°N (High Arctic Polar Amp)</text>
          </svg>
        </div>
      )}

      {/* SPM.6 Diagram */}
      {figureId === 'spm6' && (
        <div className="p-4 bg-slate-900 rounded-xl border border-slate-700 space-y-3">
          <span className="text-xs font-bold text-white block">Cumulative CO2 Emissions vs Temp Rise Scatter Plot</span>
          <svg viewBox="0 0 500 160" className="w-full h-44 bg-slate-950 rounded-lg">
            {/* Uncertainty Cone */}
            <path d="M40 130 L 460 30 L 460 80 Z" fill="rgba(251, 191, 36, 0.3)" stroke="#fbbf24" strokeWidth="1" />
            {/* Central TCRE Best Estimate */}
            <line x1="40" y1="130" x2="460" y2="55" stroke="#fbbf24" strokeWidth="3.5" />

            <text x="40" y="148" fill="#f8fafc" fontSize="10" fontWeight="bold">0 GtCO2 (1850)</text>
            <text x="240" y="148" fill="#f8fafc" fontSize="10" fontWeight="bold">2390 GtCO2 (2019)</text>
            <text x="390" y="148" fill="#f8fafc" fontSize="10" fontWeight="bold">4500 GtCO2</text>
            <text x="330" y="45" fill="#fbbf24" fontSize="10" fontWeight="bold">Best Estimate: 0.45°C / 1000 GtCO2</text>
          </svg>
        </div>
      )}

      {/* SPM.7 Diagram */}
      {figureId === 'spm7' && (
        <div className="p-4 bg-slate-900 rounded-xl border border-slate-700 space-y-3">
          <span className="text-xs font-bold text-white block">Extreme Event Frequency Multipliers (Pre-Industrial vs +4°C)</span>
          <svg viewBox="0 0 500 140" className="w-full h-36 bg-slate-950 rounded-lg">
            <text x="30" y="30" fill="#cbd5e1" fontSize="11" fontWeight="bold">50-Yr Extreme Heat Event Frequency:</text>
            <text x="30" y="60" fill="#34d399" fontSize="11" fontWeight="bold">Pre-Industrial: 1x (Once per 50 yrs)</text>
            <text x="30" y="85" fill="#38bdf8" fontSize="11" fontWeight="bold">+1.5°C GWL: 8.6x Acceleration</text>
            <text x="30" y="112" fill="#f43f5e" fontSize="13" fontWeight="bold">+4.0°C GWL: 39.2x Acceleration (39 times per 50 yrs!)</text>
          </svg>
        </div>
      )}

      {/* SPM.8 Diagram */}
      {figureId === 'spm8' && (
        <div className="p-4 bg-slate-900 rounded-xl border border-slate-700 space-y-3">
          <span className="text-xs font-bold text-white block">Multi-Century Sea Level Rise Commitment to Year 2300</span>
          <svg viewBox="0 0 500 160" className="w-full h-44 bg-slate-950 rounded-lg">
            <line x1="40" y1="130" x2="480" y2="130" stroke="#475569" strokeWidth="1.5" />
            <path d="M40 130 Q 200 125, 320 100 T 480 70" fill="none" stroke="#60a5fa" strokeWidth="3" />
            <path d="M40 130 Q 200 120, 320 80 T 480 35" fill="none" stroke="#fb923c" strokeWidth="3" />
            {/* Low Likelihood High Impact Line */}
            <path d="M40 130 Q 200 110, 320 40 T 480 10" fill="none" stroke="#f43f5e" strokeWidth="3.5" strokeDasharray="5,5" />

            <text x="310" y="20" fill="#f43f5e" fontSize="10" fontWeight="bold">High-Impact Ice Instability (+15m by 2300)</text>
            <text x="40" y="148" fill="#f8fafc" fontSize="10" fontWeight="bold">2000</text>
            <text x="250" y="148" fill="#f8fafc" fontSize="10" fontWeight="bold">2100</text>
            <text x="440" y="148" fill="#f8fafc" fontSize="10" fontWeight="bold">2300</text>
          </svg>
        </div>
      )}

      {/* SPM.9 Diagram */}
      {figureId === 'spm9' && (
        <div className="p-4 bg-slate-900 rounded-xl border border-slate-700 space-y-3">
          <span className="text-xs font-bold text-white block">33 Climate Impact-Driver (CID) Regional Matrix Overview</span>
          <svg viewBox="0 0 500 140" className="w-full h-36 bg-slate-950 rounded-lg">
            <rect x="30" y="20" width="100" height="28" fill="#f43f5e" rx="4" />
            <rect x="140" y="20" width="100" height="28" fill="#22d3ee" rx="4" />
            <rect x="250" y="20" width="100" height="28" fill="#60a5fa" rx="4" />
            <rect x="360" y="20" width="100" height="28" fill="#c084fc" rx="4" />

            <text x="42" y="38" fill="#ffffff" fontSize="10" fontWeight="bold">Heat & Cold (3)</text>
            <text x="152" y="38" fill="#ffffff" fontSize="10" fontWeight="bold">Wet & Dry (3)</text>
            <text x="257" y="38" fill="#ffffff" fontSize="10" fontWeight="bold">Coastal/Ocean (3)</text>
            <text x="372" y="38" fill="#ffffff" fontSize="10" fontWeight="bold">Snow & Ice (2)</text>

            <text x="30" y="85" fill="#f8fafc" fontSize="11" fontWeight="bold">● Increases in 95% of inhabited land & coastal regions</text>
          </svg>
        </div>
      )}

      {/* SPM.10 Diagram */}
      {figureId === 'spm10' && (
        <div className="p-4 bg-slate-900 rounded-xl border border-slate-700 space-y-3">
          <span className="text-xs font-bold text-white block">Historical Emissions vs Remaining 1.5°C / 2.0°C Carbon Budgets</span>
          <svg viewBox="0 0 500 150" className="w-full h-40 bg-slate-950 rounded-lg">
            {/* Historical Bar */}
            <rect x="30" y="30" width="300" height="22" fill="#64748b" rx="4" stroke="#94a3b8" strokeWidth="1" />
            <text x="35" y="45" fill="#ffffff" fontSize="10" fontWeight="bold">Historical Emissions: 2560 GtCO2 (1850–2019)</text>

            {/* 1.5C Budget Bar */}
            <rect x="330" y="30" width="60" height="22" fill="#22d3ee" rx="4" stroke="#a5f3fc" strokeWidth="1" />
            <text x="335" y="45" fill="#020617" fontSize="9" fontWeight="bold">500 Gt (1.5°C)</text>

            {/* 2.0C Budget Bar */}
            <rect x="330" y="70" width="120" height="22" fill="#fbbf24" rx="4" stroke="#fef08a" strokeWidth="1" />
            <text x="335" y="85" fill="#020617" fontSize="9" fontWeight="bold">1150 Gt (2.0°C)</text>
          </svg>
        </div>
      )}

      {/* SR15 SPM.1 Diagram */}
      {figureId === 'sr15_spm1' && (
        <div className="p-4 bg-slate-900 rounded-xl border border-slate-700 space-y-3">
          <span className="text-xs font-bold text-white block">Monthly GMST Anomaly & Linear 1.5°C Projection</span>
          <svg viewBox="0 0 500 160" className="w-full h-44 bg-slate-950 rounded-lg">
            <line x1="40" y1="130" x2="460" y2="130" stroke="#475569" strokeWidth="1.5" />
            <line x1="40" y1="40" x2="460" y2="40" stroke="#f43f5e" strokeDasharray="4,4" strokeWidth="2" />

            <path d="M40 120 L 150 125 L 280 80 L 370 70" fill="none" stroke="#38bdf8" strokeWidth="3" />
            <path d="M370 70 L 440 40" fill="none" stroke="#fbbf24" strokeWidth="3" strokeDasharray="4,4" />

            <text x="45" y="32" fill="#f43f5e" fontSize="10" fontWeight="bold">1.5°C Threshold Line</text>
            <text x="370" y="58" fill="#fbbf24" fontSize="10" fontWeight="bold">Reaches 1.5°C in ~2040</text>
            <text x="40" y="148" fill="#f8fafc" fontSize="10" fontWeight="bold">1850</text>
            <text x="350" y="148" fill="#f8fafc" fontSize="10" fontWeight="bold">2017 (1.0°C)</text>
            <text x="430" y="148" fill="#f8fafc" fontSize="10" fontWeight="bold">2040</text>
          </svg>
        </div>
      )}

      {/* SR15 SPM.2 Diagram */}
      {figureId === 'sr15_spm2' && (
        <div className="p-4 bg-slate-900 rounded-xl border border-slate-700 space-y-3">
          <span className="text-xs font-bold text-white block">4 Archetype Pathway Curves (P1, P2, P3, P4)</span>
          <svg viewBox="0 0 500 160" className="w-full h-44 bg-slate-950 rounded-lg">
            <line x1="40" y1="90" x2="460" y2="90" stroke="#94a3b8" strokeWidth="2" />
            <text x="45" y="85" fill="#e2e8f0" fontSize="9" fontWeight="bold">Net Zero CO2</text>

            {/* P1 Curve */}
            <path d="M40 40 L 150 70 L 250 95 L 460 110" fill="none" stroke="#22d3ee" strokeWidth="3.5" />
            {/* P4 Curve (High Overshoot) */}
            <path d="M40 40 L 150 35 L 250 50 L 350 90 L 460 140" fill="none" stroke="#f43f5e" strokeWidth="3.5" />

            <text x="390" y="125" fill="#22d3ee" fontSize="10" fontWeight="bold">P1 (Low Demand)</text>
            <text x="390" y="150" fill="#f43f5e" fontSize="10" fontWeight="bold">P4 (Heavy BECCS)</text>
          </svg>
        </div>
      )}

      {/* SR15 SPM.3 Diagram */}
      {figureId === 'sr15_spm3' && (
        <div className="p-4 bg-slate-900 rounded-xl border border-slate-700 space-y-3">
          <span className="text-xs font-bold text-white block">Vertical "Burning Embers" Risk Bars across Warming Levels</span>
          <svg viewBox="0 0 500 160" className="w-full h-44 bg-slate-950 rounded-lg">
            {/* Burning Ember Bar 1 */}
            <rect x="60" y="100" width="35" height="30" fill="#fef08a" stroke="#facc15" strokeWidth="1" />
            <rect x="60" y="60" width="35" height="40" fill="#f43f5e" stroke="#fda4af" strokeWidth="1" />
            <rect x="60" y="20" width="35" height="40" fill="#c084fc" stroke="#e9d5ff" strokeWidth="1" />

            {/* Burning Ember Bar 2 */}
            <rect x="180" y="110" width="35" height="20" fill="#fef08a" stroke="#facc15" strokeWidth="1" />
            <rect x="180" y="75" width="35" height="35" fill="#f43f5e" stroke="#fda4af" strokeWidth="1" />
            <rect x="180" y="20" width="35" height="55" fill="#c084fc" stroke="#e9d5ff" strokeWidth="1" />

            <text x="45" y="148" fill="#f8fafc" fontSize="10" fontWeight="bold">Coral Reefs</text>
            <text x="155" y="148" fill="#f8fafc" fontSize="10" fontWeight="bold">Arctic Summer Ice</text>
          </svg>
        </div>
      )}

      {/* SR15 SPM.4 Diagram */}
      {figureId === 'sr15_spm4' && (
        <div className="p-4 bg-slate-900 rounded-xl border border-slate-700 space-y-3">
          <span className="text-xs font-bold text-white block">SDG Interaction Matrix Schematic</span>
          <svg viewBox="0 0 500 140" className="w-full h-36 bg-slate-950 rounded-lg">
            <rect x="40" y="25" width="140" height="22" fill="#34d399" rx="4" stroke="#a7f3d0" strokeWidth="1" />
            <rect x="40" y="55" width="140" height="22" fill="#34d399" rx="4" stroke="#a7f3d0" strokeWidth="1" />
            <rect x="40" y="85" width="140" height="22" fill="#f43f5e" rx="4" stroke="#fda4af" strokeWidth="1" />

            <text x="45" y="40" fill="#020617" fontSize="10" fontWeight="bold">Energy Demand (+ Synergies)</text>
            <text x="45" y="70" fill="#020617" fontSize="10" fontWeight="bold">Land Protection (+ Synergies)</text>
            <text x="45" y="100" fill="#ffffff" fontSize="10" fontWeight="bold">Large BECCS (- Trade-offs)</text>
          </svg>
        </div>
      )}
    </div>
  );
};
